import { mapState } from 'vuex'
import moment from 'moment-timezone'
import contentWithCompletionTimer from '@/constants/completionTimer'
import { sendNetcore } from '@/functions/tracker/netcore'
import quizCategories from '@/constants/quizCategories'
import redeemStatus from '@/constants/redeemStatus'
import programTrainingTypes from '@/constants/programTrainingTypes'
import ProgramActivityAPI from '@/api/program-activity'
import getTrainingType from '@/functions/getTrainingType'
import getAssessmentType from '@/functions/getAssessmentType'
import { showErrorAlert } from '@/functions/showErrorAlert'
import { LIMIT_ATTEMPT_5_TIMES_FR } from '@/constants/pmoFrConfig'

export default {
  computed: {
    ...mapState('activityV2', [
      'showPrakerjaFaceRecognition',
      'isVisibleRedeemCodeModal'
    ]),
    authorization () {
      return this.$store.state.storage.accessToken
    },
    activityValidate () {
      return this.$store.state.activityV2.validate
    },
    activityFocus () {
      return this.$store.state.activityV2.activityFocus
    },
    activityResourceDetail () {
      return this.$store.state.activityV2.activityResourceDetail
    },
    userActivityId () {
      return this.$store.state.activityV2.userActivityId
    },
    isActivityStarted () {
      return this.$store.state.activityV2.isActivityStarted
    },
    tourData () {
      return this.$store.state.activityV2.tourData
    },
    isGroupActivity () {
      return this.$store.state.activityV2.activityResourceDetail.is_group_activity === 1
    },
    isGroupActivityManualByUser () {
      return this.$store.state.activityV2.activityResourceDetail.group_activity_type === 'manual_by_user'
    },
    isFaceRecognitionNotComplete () {
      return (!!this.faceRecognitionUrl ||
        !!this.activityFocus?.pmo_response?.success) &&
        this.activityFocus?.sequence_status !== LIMIT_ATTEMPT_5_TIMES_FR
    },
    isUserJoinAGroup () {
      return this.$store.state.activityV2.activityResourceDetail.is_join_group
    },
    isQuiz () {
      const quiz = ['refleksi', 'quiz']
      return quiz.includes(this.activityResourceDetail.type)
    },
    isQuizV2 () {
      // Temporary logic to show quiz v2 components
      return this.activityResourceDetail.type === 'quiz-v2'
    },
    isTour () {
      return this.$store.state.activityV2.isTour
    },
    isTourFinished () {
      return this.$store.state.activityV2.isTourFinished
    },
    isContentCompletionTimerAllowed () {
      return contentWithCompletionTimer.includes(this.activityResourceDetail.type)
    },
    isDevEnv () {
      return process.env.VUE_APP_URL_SERVER.includes('dev')
    },
    isCurrentActivityComplete () {
      return this.$store.state.activityV2.activityResourceDetail.is_complete
    },
    isExperimental () {
      return this.$store.state.activityV2.isExperimental
    },
    hasCompletionTimer () {
      return this.$store.state.activityV2.activityResourceDetail.completion_timer >= 0
    },
    program () {
      return this.$store.state.activityV2.activityDashboard
    },
    selectedGroupActivity () {
      return this.$store.state.activityV2.activityResourceDetail.content_group.find(group => group.selected_group)
    },
    source () {
      return this.$store.state.analytics.source
    },
    userId () {
      return this.$store.state.storage.userId
    },
    userInfo () {
      return this.$store.state.user.userInfo
    },
    userLandingTimestamp () {
      return this.$store.state.activityV2.userLandingTimestamp
    },
    isPrakerjaCategory () {
      return ['KARIERMU', 'PRAKERJA', 'KAMPUS_MERDEKA'].includes(this.program.category_name)
    },
    isPrakerjaPurchaseType () {
      return this.activityValidate?.purchase_type?.toLowerCase() === 'prakerja'
    },
    isAsesorPrakerja () {
      return !!this.activityValidate?.is_asesor
    },
    activityV2 () {
      return this.$store.state.activityV2
    },
    isPostTest () {
      return this.activityResourceDetail.assessment_type === quizCategories.POSTTEST
    },
    isWebinarProgram () {
      return this.program.training_type === programTrainingTypes.WEBINAR
    },
    isOfflineProgram () {
      return this.program.training_type === programTrainingTypes.OFFLINE
    },
    isSelfPacedLearning () {
      return this.program.training_type === programTrainingTypes.SELF_PACED_LEARNING
    },
    isRegularProgram () {
      // Purchase type prakerja dengan training type reguler
      return this.program.training_type === programTrainingTypes.REGULAR
    },
    faceRecognitionUrl () {
      return this.activityFocus.face_recognition_url ? this.activityFocus.face_recognition_url : ''
    },
    errorMessageGenerateUrlPrakerja () {
      return this.activityFocus.pmo_response ? this.activityFocus.pmo_response.message : ''
    },
    isLoadingFaceRecognition () {
      return this.$store.state.activityV2.isLoadingFaceRecognition
    },
    currentActivityNumber () {
      return this.$store.state.activityV2.currentActivityNumber
    },
    isAccessedFromKariermuB2bApp () {
      return window.atob(this.$cookies.get('source-access')) === 'kariermu-b2b' || ''
    }
  },
  methods: {
    setActivityComplete (hasCompletionTimer = this.hasCompletionTimer, showWarning = true) {
      if (this.isContentCompletionTimerAllowed && hasCompletionTimer) {
        return
      }
      const activityId = this.activityResourceDetail.id
      this.$store.dispatch('activityV2/setActivityComplete', { activityId, showWarning })
    },
    showAlert (data, customMessage = null, errorPath) {
      showErrorAlert(data, null, customMessage, errorPath)
    },
    async getProgramLastAccess () {
      const { data } = await ProgramActivityAPI.getSessionCompletion({
        Authorization: this.authorization,
        programId: this.activityValidate.program_id,
        session: this.activityValidate.last_fr_session || 1
      })

      return data
    },
    getQuizTypeEventParam () {
      return this.activityResourceDetail.type === 'quiz' ? getAssessmentType(this.activityResourceDetail.assessment_type) : 'NA'
    },
    formatDate (date) {
      moment.tz.setDefault('Asia/Jakarta')
      return moment(date).isValid() ? moment(date).format('YYYY-MM-DD HH:mm:ss') : 'NA'
    },
    async setCompletedStreakLiveProgram () {
      try {
        const { data } = await ProgramActivityAPI.setCompletedStreakLiveProgram({
          Authorization: this.authorization,
          resource_id: this.activityResourceDetail.id
        })
        if (data.status === 200) {
          return data
        }
      } catch (error) {
        console.error(error)
      }
    },
    async logActivityEvent (eventName) {
      const endTime = Date.now()
      const engagementTime = Math.round((endTime - this.userLandingTimestamp) / 1000)
      const categoryName = this?.program?.category_name?.toLowerCase() || 'NA'
      const categoryProgramString = this?.program?.price_normal ? `${categoryName}_program_${Number(this.program.price_normal) ? 'paid' : 'free'}` : 'NA'
      const currentRouteFullPath = this.$route.fullPath
      const dataProgramLastAccess = await this.getProgramLastAccess()

      const eventParams = {
        is_platinum_user: this.userInfo.is_platinum_user,
        is_prakerja_user: !!this.userInfo.prakerja_id,
        screen_engagement_time: engagementTime,
        screen_url: `https://${window.location.hostname}${currentRouteFullPath}`,
        program_id: this.activityValidate.program_id,
        program_progress: this?.program?.percentage_progress ? Math.floor(this.program.percentage_progress) : 0, // Todo: Butuh improvement untuk get completion program
        program_name: this?.program?.name || 'NA',
        program_type: this.activityValidate.purchase_type.toLowerCase() === 'prakerja' ? 'prakerja' : 'nonprakerja',
        program_last_access: 'NA',
        source: this.source,
        user_id: this.userInfo.id,
        user_institution_id: this.userInfo.institution_id,
        user_profession: this.userInfo.profession_name,
        user_year_level: this.userInfo.year_level_name,
        is_blocked: String(this.showPrakerjaFaceRecognition || this.isVisibleRedeemCodeModal),
        smm_user: categoryName === 'smm' ? categoryProgramString : null,
        sekolahmu_user: categoryName === 'sekolahmu' ? categoryProgramString : null,
        kariermu_user: categoryName === 'kariermu' ? categoryProgramString : null,
        training_type: getTrainingType(this.activityValidate.training_type) || 'NA',
        quiz_type: this.getQuizTypeEventParam(),
        redeem_status: this.activityValidate.has_redeem_program === redeemStatus.REDEEM_SUCCESS || false,
        activity_slug: this.activityResourceDetail.slug,
        activity_number: this.currentActivityNumber,
        activity_name: this.activityResourceDetail.title,
        total_activity: this?.program?.total_resources || 0,
        next_activity_slug: this.activityFocus.resource_slug_after,
        next_activity_name: this.activityFocus.resource_title_after,
        sequence: this.activityValidate.last_fr_session
      }

      if (dataProgramLastAccess?.status) {
        if (dataProgramLastAccess.status === 200) {
          eventParams.program_last_access = dataProgramLastAccess?.data.finish_at || dataProgramLastAccess?.data.enrolled_at
          eventParams.program_last_access = this.formatDate(eventParams.program_last_access)
        }

        window.firebaseAnalytics.logEvent(eventName, eventParams)
        sendNetcore(eventName, this.userInfo, eventParams)
      } else {
        console.error('Log Activity Event Failed')
      }
    }
  }
}
